import React from "react";
import ErrorBoundaryUI from "@ui/ErrorBoundary";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { ApiError, userMessage } from "../api/error";
import t from "../i18n";
import { useAuth } from "react-oidc-context";

const ErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const { reset: resetReactQueryCache } = useQueryErrorResetBoundary();
  const auth = useAuth();
  const handleError = React.useCallback(
    (
      error: unknown,
    ): {
      title: string;
      subtitle: string | null;
      cta: string;
      onCTAClick: (() => void) | null;
    } => {
      const [title, subtitle] =
        error instanceof Error
          ? [userMessage(error), error.message]
          : typeof error === "string"
            ? [error, null]
            : [JSON.stringify(error) ?? "empty error", null];
      const cta =
        error instanceof ApiError && error.type === "unauthorized"
          ? t("fallback.reconnect")
          : t("fallback.retry");
      const onCTAClick = async () => {
        if (error instanceof ApiError) {
          switch (error.type) {
            case "unauthorized":
              await auth.removeUser();
              await auth.signinRedirect();
              break;
            case "conflict":
            case "forbidden":
            case "bad_request":
            case "not_found":
            case "contract_failure":
            case "tmp_backend_error":
            case "backend_error":
              resetReactQueryCache();
              break;
            case "network":
              window.location.reload();
              break;
            default:
              throw new Error(error.type satisfies never);
          }
        } else {
          window.location.reload();
        }
      };

      return {
        title,
        subtitle,
        cta,
        onCTAClick,
      };
    },
    [auth, resetReactQueryCache],
  );

  return <ErrorBoundaryUI onError={handleError}>{children}</ErrorBoundaryUI>;
};

export default ErrorBoundary;
